import { CREATE_ENTITY_TYPE_ATTRIBUTE_URL, LIST_ENTITY_TYPE_ATTRIBUTES_URL, UNASSIGN_ENTITY_TYPE_ATTRIBUTE_URL , GET_QUESTIONS_IN_PROJECT, get_headers } from "@/utils/constants/constants.js";
import GenericEntityAttribute from '@/components/probaworkflows_base_components/GenericEntityAttributeComponent/GenericEntityAttribute.vue';
import alert_mixin from '@/utils/mixins/alert_mixin';
import axios from "axios";
import { get_project_id } from '@/utils/constants/constants.js';

export default {
  name: "EntityManagementAttributes",
  mixins: [alert_mixin],
  components: {
    GenericEntityAttribute
  },
  props: {
    entity_type_id: {
      type: [String, Number],
      required: true
    }
  },
  data:() => ({
      headers: get_headers(),
      attributes: [],
      available_questions: [],
      questions_loading: true,
      loading: false,
      save_loading: false,
      show_create_modal: false,
      is_edit_modal_open: false,
      viewing_attribute: null,
      editing_attribute: null,
      question_search: '',
      pending_assignments: new Map(),
      columns: [
        { key: 'question_text', label: 'Question' },
        { key: 'question_type', label: 'Type' },
        { key: 'date_created', label: 'Date Created' }
      ]
  }),
  computed: {
    current_project_id() {
      return get_project_id();
    },

    has_changes() {
      return this.pending_assignments.size > 0;
    },

    filtered_questions() {
      if (!this.question_search) {
        return this.available_questions;
      }
      const search_term = this.question_search.toLowerCase();
      return this.available_questions.filter(question => 
        question.question_text?.toLowerCase().includes(search_term) ||
        question.question_type?.toLowerCase().includes(search_term) ||
        question.id?.toString().includes(search_term)
      );
    },

    assigned_count() {
      return this.available_questions.filter(q => this.is_question_assigned(q.id)).length;
    },

    unassigned_count() {
      return this.available_questions.filter(q => !this.is_question_assigned(q.id)).length;
    }
  },
  async mounted() {
    if (!this.validate_entity_type_id()) {
      return;
    }
    await this.fetch_attributes();
  },
  watch: {
    show_create_modal(newVal) {
      if (newVal) {
        this.fetch_questions_for_assignment();
      } else {
        // Reset when modal closes
        this.available_questions = [];
        this.question_search = '';
        this.pending_assignments.clear();
      }
    }
  },
  methods: {
    validate_entity_type_id() {
      if (!this.entity_type_id) {
        return false;
      }
      return true;
    },

    async fetch_attributes() {
      if (!this.validate_entity_type_id()) return;
      
      this.loading = true;
      try {
        const params = {
          entity_type_id: this.entity_type_id
        };
        
        axios.get(LIST_ENTITY_TYPE_ATTRIBUTES_URL, {
          params: params,
          headers: this.headers
        })
        .then((response) => {
          const response_data = response.data;
          if (response_data.status === 'success') {
            this.attributes = response_data.data;
          } else {
            this.show_error(response_data.message || 'Failed to fetch attributes');
          }
        })
        .catch((error) => {
          this.show_error(this.get_error_message(error));
        });
      } catch (error) {
        this.show_error(this.get_error_message(error));
      } finally {
        this.loading = false;
      }
    },

    async fetch_questions_for_assignment() {
      if (!this.validate_entity_type_id()) return;
      
      this.questions_loading = true;
      try {
        const payload = { 
          project_id: this.current_project_id,
          entity_type_id: this.entity_type_id
        };

        axios.post(GET_QUESTIONS_IN_PROJECT, payload, {
          headers: this.headers
        })
        .then((response) => {
          const response_data = typeof response.data === 'string' 
            ? JSON.parse(response.data) 
            : response.data;

          if (response_data.status === 'success') {
            this.available_questions = response_data.data;
          } else {
            this.show_error(response_data.message || 'Failed to fetch questions');
            this.available_questions = [];
          }
        })
        .catch((error) => {
          this.show_error(this.get_error_message(error));
          this.available_questions = [];
        });
      } catch (error) {
        this.show_error(this.get_error_message(error));
        this.available_questions = [];
      } finally {
        this.questions_loading = false;
      }
    },

    is_question_assigned(question_id) {
      // Check if there's a pending assignment change first
      if (this.pending_assignments.has(question_id)) {
        return this.pending_assignments.get(question_id);
      }
      // Otherwise check current assignment status
      return this.attributes.some(attr => attr.question_id == question_id);
    },

    async on_toggle_assign(question) {
      const new_assignment_state = !this.is_question_assigned(question.id);
      this.pending_assignments.set(question.id, new_assignment_state);
    },

    async save_assignments() {
      if (!this.can_save_assignments()) return;

      this.save_loading = true;
      try {
        const assignment_promises = this.build_assignment_requests();
        const responses = await Promise.all(assignment_promises);

        if (this.all_responses_successful(responses)) {
          this.handle_success_feedback();
        } else {
          this.show_error('Some changes failed to save. Please try again.');
        }
      } catch (error) {
        this.show_error(this.get_error_message(error));
      } finally {
        this.save_loading = false;
      }
    },

    can_save_assignments() {
      if (!this.validate_entity_type_id()) return false;
      if (this.pending_assignments.size === 0) {
        this.show_error('No changes to save');
        return false;
      }
      return true;
    },

    build_assignment_requests() {
      const requests = [];
      for (const [question_id, should_assign] of this.pending_assignments) {
        const payload = {
          attribute_id: question_id,
          entity_type_id: this.entity_type_id
        };
        const url = should_assign
          ? CREATE_ENTITY_TYPE_ATTRIBUTE_URL
          : UNASSIGN_ENTITY_TYPE_ATTRIBUTE_URL;

        requests.push(
          axios.post(url, payload, { headers: this.headers })
        );
      }
      return requests;
    },

    all_responses_successful(responses) {
      return responses.every(response => response.data.status === 'success');
    },

    handle_success_feedback() {
      const assigned_count = Array.from(this.pending_assignments.values()).filter(Boolean).length;
      const unassigned_count = this.pending_assignments.size - assigned_count;

      let message = 'Changes saved successfully';
      if (assigned_count > 0 && unassigned_count > 0) {
        message = `${assigned_count} questions assigned and ${unassigned_count} unassigned successfully`;
      } else if (assigned_count > 0) {
        message = `${assigned_count} question${assigned_count > 1 ? 's' : ''} assigned successfully`;
      } else if (unassigned_count > 0) {
        message = `${unassigned_count} question${unassigned_count > 1 ? 's' : ''} unassigned successfully`;
      }

      this.show_success(message);
      this.pending_assignments.clear();
      this.fetch_attributes();
      this.close_create_modal();
    },

    close_create_modal() {
      this.show_create_modal = false;
      this.question_search = '';
      this.pending_assignments.clear();
    },

    open_edit_modal(attribute) {
      this.editing_attribute = attribute;
      this.is_edit_modal_open = true;
      this.viewing_attribute = null;
    },

    close_edit_modal() {
      this.is_edit_modal_open = false;
      this.editing_attribute = null;
    },

    view_attribute(attribute) {
      this.viewing_attribute = attribute;
    },

    delete_attribute(attribute) {
      if (!this.validate_entity_type_id()) return;
      
      // Use your custom confirmation dialog
      this.show_confirm(
        'Unassign Attribute',
        'Are you sure you want to unassign this attribute?',
        () => {
          // Confirm callback - this runs when user clicks "Confirm"
          this.loading = true;
          
          const payload = {
            attribute_id: attribute.question_id || attribute.id,
            entity_type_id: this.entity_type_id
          };

          axios.post(UNASSIGN_ENTITY_TYPE_ATTRIBUTE_URL, payload, {
            headers: this.headers
          })
          .then((response) => {
            const response_data = response.data;
            if (response_data.status === 'success') {
              this.show_success('Attribute unassigned successfully');
              // Remove the unassigned attribute from the frontend
              this.attributes = this.attributes.filter(attr => attr.id !== attribute.id);
            } else {
              this.show_error(response_data.message || 'Failed to unassign attribute');
            }
          })
          .catch((error) => {
            this.show_error(this.get_error_message(error));
          })
          .finally(() => {
            this.loading = false;
          });
        },
      );
    },

    format_question_type(type) {
      const type_map = {
        short_text: 'Short Text',
        long_text: 'Long Text',
        number: 'Number',
        date: 'Date',
        dropdown: 'Dropdown',
        checkbox: 'Checkbox',
        radio: 'Radio',
        file: 'File Upload'
      };
      return Reflect.get(type_map, type) || type.replace(/_/g, ' ');
    },

    format_date(date_string) {
      if (!date_string) return '-';
      return new Date(date_string).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    get_error_message(error) {
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          return error.response.data.message;
        } else if (error.response.data && typeof error.response.data === 'string') {
          // Create a promise-like handling for JSON parsing
          return new Promise((resolve) => {
            try {
              const parsed = JSON.parse(error.response.data);
              resolve(parsed.message || 'An error occurred');
            } catch (e) {
              resolve(error.response.data || 'An error occurred');
            }
          }).then(result => result);
        }
      }
      return error.message || 'An unexpected error occurred';
    }
  }
};